import { React, PT, withRouter } from '@gasbuddy/react';
import moment from 'moment';
import { Table, Menu, Icon } from 'semantic-ui-react';
import BN from 'bignumber.js';
import { reasonForUnbalanced } from '../../../lib/transaction';

function isBad(tx) {
  if ('split_sum' in tx && !(new BN(tx.split_sum || 0).isZero())) {
    return true;
  }
  return false;
}

function styleForTx(tx) {
  if (tx.status === 'deleted') {
    return {
      textDecoration: 'line-through',
    };
  }
  return {};
}

function txAmountValue(tx, showWex) {
  if (showWex) {
    if (tx.wex_amount) {
      return new BN(tx.wex_amount).toFixed(2);
    }
    const wex = tx.splits?.find(s => s.split_type === 'purchase' && s.counterparty === 'wex');
    if (wex) {
      return new BN(wex.amount).toFixed(2);
    }
  }
  return new BN(tx.amount).toFixed(2);
}

const BaseTxTable = ({
  tx, history, showPrevious, showWexSplit, onNext, onPrev, range,
  pageSize, showAccount, showReason, txDetails,
}) => (
  <Table basic="very" selectable style={{ tableLayout: 'fixed', fontSize: '0.9em' }}>
    <Table.Header>
      <Table.Row>
        <Table.HeaderCell width={2} textAlign="right">Tx Id</Table.HeaderCell>
        {showAccount
          && <Table.HeaderCell width={2} textAlign="right">Account</Table.HeaderCell>
        }
        <Table.HeaderCell width={3} textAlign="center">Date</Table.HeaderCell>
        <Table.HeaderCell>Location</Table.HeaderCell>
        {showReason
          && <Table.HeaderCell>Reason</Table.HeaderCell>
        }
        <Table.HeaderCell width={3} textAlign="right">Amount</Table.HeaderCell>
      </Table.Row>
    </Table.Header>
    <Table.Body>
      {(tx || []).slice(0, pageSize).map(t => (
        <Table.Row style={styleForTx(t)} key={t.transaction_id} error={isBad(t)} onClick={() => history.push(`/payments/transaction/${t.account_id}/${t.transaction_id}`)}>
          <Table.Cell textAlign="right">
            <a href={`/payments/transaction/${t.account_id}/${t.transaction_id}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
              {t.transaction_id}
            </a>
          </Table.Cell>
          {showAccount
            && <Table.Cell textAlign="right"><a href={`/identity/users/${t.account_id}`}>{t.account_id}</a></Table.Cell>
          }
          <Table.Cell textAlign="center" style={{ paddingLeft: '3px' }}>{moment(t.tx_date || t.created).format('M/DD/YY HH:mm')}</Table.Cell>
          <Table.Cell>
            {t.location_name}
            <br />
            {t.street1}
            <br />
            {t.locality}
            ,
            {' '}
            {t.region}
          </Table.Cell>
          {showReason
            && <Table.Cell>{reasonForUnbalanced(t)}</Table.Cell>
          }
          <Table.Cell textAlign="right">
            {t.amount ? `$${txAmountValue(t, showWexSplit)}` : 'n/a'}
            {t.nsf && (
              <p style={{ color: 'red' }}>
                {`+$${new BN(t.nsf).toFixed(2)} NSF`}
              </p>
            )}
            {t.loss && (
              <p style={{ color: 'red' }}>
                {`+$${new BN(t.loss).toFixed(2)} loss`}
              </p>
            )}
            {t.reorder_fee && (
              <p style={{ color: 'red' }}>
                {`+$${new BN(t.reorder_fee).toFixed(2)} Card Re-order Fee`}
              </p>
            )}
          </Table.Cell>
        </Table.Row>
      ))}
    </Table.Body>
    <Table.Footer>
      <Table.Row>
        <Table.Cell colSpan={2}>
          {range}
          {' '}
          {txDetails && txDetails.count > pageSize && `of ${txDetails.count}`}
        </Table.Cell>
        <Table.Cell colSpan={2 + (showAccount ? 1 : 0) + (showReason ? 1 : 0)} textAlign="right">
          {txDetails && txDetails.count > 0
            && (
            <span style={{ paddingRight: 10, textAlign: 'left', fontWeight: 'bold' }}>
              TPV $
              {new BN(txDetails.tpv || 0).round().toFixed(0)}
              {' '}
|
              {txDetails.pending && (
              <span>
                <span style={{ color: 'green' }}>
PEND $
                  {new BN(txDetails.pending).round().toFixed(0)}
                </span>
                {' '}
|
              </span>
              )}
              {txDetails.due && (
              <span>
                <span style={{ color: 'red' }}>
DUE $
                  {new BN(txDetails.due).toFixed(2)}
                </span>
                {' '}
|
              </span>
              )}
              AVE: $
              {new BN(txDetails.tpv).dividedBy(txDetails.count).round().toFixed(0)}
            </span>
            )
          }
          {(showPrevious || (tx && tx.length > pageSize))
            && (
            <Menu pagination>
              {showPrevious && (
              <Menu.Item as="a" icon onClick={onPrev}>
                <Icon name="left chevron" />
              </Menu.Item>
              )}
              {tx && tx.length > pageSize && (
              <Menu.Item as="a" icon onClick={onNext}>
                <Icon name="right chevron" />
              </Menu.Item>
              )}
            </Menu>
            )
          }
        </Table.Cell>
      </Table.Row>
    </Table.Footer>
  </Table>
);

BaseTxTable.propTypes = {
  tx: PT.arrayOf(PT.shape({
    transaction_id: PT.number.isRequired,
    created: PT.string.isRequired,
    location_name: PT.string.isRequired,
    street1: PT.string.isRequired,
    locality: PT.string.isRequired,
    region: PT.string.isRequired,
  })),
  onNext: PT.func.isRequired,
  onPrev: PT.func.isRequired,
  showPrevious: PT.bool,
  range: PT.string.isRequired,
  pageSize: PT.number,
  showAccount: PT.bool,
  showReason: PT.bool,
  txDetails: PT.shape({
    count: PT.number,
    tpv: PT.string,
    due: PT.string,
  }),
  showWexSplit: PT.bool,
  history: PT.shape({ push: PT.func }).isRequired,
};

BaseTxTable.defaultProps = {
  tx: [],
  showPrevious: false,
  pageSize: 5,
  txDetails: null,
  showAccount: false,
  showReason: false,
  showWexSplit: false,
};

export const TxTable = withRouter(BaseTxTable);
